import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Colors, ModalProps } from 'Types';
import { Button, ModalBox, ModalBoxFooter, FontAwesome } from 'Elements';
import axios from 'Adapter';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styles from 'partials/step/step-container.module.scss';
import { clx, currencyFormatter } from 'src/helpers/Utils';
import { usePaymentMethodList } from 'src/hooks/useBusinessPartner';
import { useAxios } from 'src/hooks/useAxios';
import { api } from 'src/helpers/API';
import { config } from 'src/helpers/Config';
import { toastify } from 'src/helpers/Toastify';

interface Props extends ModalProps {
	totalAmount: number;
	subscriptionDuration: number;
	subscriptionPaymentStatus: number;
	urlBack: string;
	subscriptionId: string;
	returnUrl: string;
	selectedPlanId?: string;
	selectedPlanType?: number;
	communityPartnerId?: string;
	voucherCodeCurrent?: string;
	currentSubscriptionPaymentId: string;
	isVoucher?: boolean;
	toggleModalResulte: () => void;
	setSubscriptionPaymentStatus: (value: number) => void;
	refetch?: () => void;
}

export const ModalBoxPaymentMethodList: FC<Props> = ({
	isOpen,
	totalAmount,
	urlBack,
	returnUrl,
	subscriptionId,
	voucherCodeCurrent,
	subscriptionDuration,
	subscriptionPaymentStatus,
	currentSubscriptionPaymentId,
	isVoucher = false,
	selectedPlanId,
	communityPartnerId,
	selectedPlanType,
	toggleModalResulte,
	setSubscriptionPaymentStatus,
	onClose,
	refetch
}) => {
	const { t } = useTranslation();
	const [_searchParams, setSearchParams] = useSearchParams();
	const [show, setShow] = useState(false);
	const [errorCode, setErrorCode] = useState('');
	const [totalAmountLabel, setTotalAmountLabel] = useState(totalAmount);
	const [voucherCodeCurrentLabel, setVoucherCodeCurrentLabel] =
		useState(voucherCodeCurrent);
	const [voucherCode, setVoucherCode] = useState('');
	const [canApplyVoucher, activateApplyVoucher] = useState(true);
	const { sendRequest: subscriptionPaymentsDetail } = useAxios<any>();
	const { sendRequest, isLoading } = useAxios<any>();
	const [disabled, setDisabled] = useState(false);
	const [loadingVoucher, setLoadingVoucher] = useState(false);
	const [intervalDelay, setIntervalDelay] = useState(5000);
	const { data: paymentMethodList, isLoading: isLoadingPaymentMethodList } =
		usePaymentMethodList({
			enabled: isOpen
		});
	const navigate = useNavigate();

	const onClickAddPayment = async () => {
		const url: any = `${config.redirectUrl}${returnUrl}`;
		setDisabled(true);
		await sendRequest(
			api.addPaymentMethod,
			{ data: { returnUrl: url } },
			async (data: any) => {
				window.location = data;
			}
		);
	};

	const onClickPayment = async (paymentMethodId) => {
		onClose?.();
		setDisabled(true);
		setSearchParams({
			showModal: 'true',
			subscriptionPaymentStatus: '3',
			subscriptionId: subscriptionId
		});
		toggleModalResulte();
		await sendRequest(
			api.subscriptionPay(currentSubscriptionPaymentId),
			{ data: { paymentMethodId: paymentMethodId } },
			async () => {
				setSubscriptionPaymentStatus(totalAmountLabel === 0 ? 7 : 3);
				// checkPayStatus();
			}
		)
			.catch(() => {
				toggleModalResulte();
			})
			.finally(() => {
				setDisabled(true);
			});
	};

	const onClickApplyVoucher = async () => {
		setLoadingVoucher(true);
		await axios
			.put(
				api.putApplyVoucher(
					subscriptionId as string,
					voucherCode as string
				)
			)
			.then(() => {
				refetch && refetch();
				toastify('toastify.voucher-apply-successfully', {
					type: 'success'
				});
				setLoadingVoucher(false);
				if (totalAmountLabel === 0) {
					onClickPayment('');
				} else {
					activateApplyVoucher(true);
					setShow(false);
					setVoucherCode('');
					setErrorCode('');
					getPaymentData();
				}
			});
	};

	const checkPayStatus = async () => {
		if (currentSubscriptionPaymentId) {
			await subscriptionPaymentsDetail(
				api.getSubscriptionPaymentsDetail(currentSubscriptionPaymentId),
				{ method: 'GET' },
				(data) => {
					if (data?.status) {
						setSubscriptionPaymentStatus(data?.status);
					}
				}
			);
		}
	};

	const getPaymentData = async () => {
		if (currentSubscriptionPaymentId) {
			await sendRequest(
				api.getSubscriptionPaymentsDetail(currentSubscriptionPaymentId),
				{ method: 'GET' },
				(paymentData) => {
					setTotalAmountLabel(paymentData.price.totalAmount as any);
					setVoucherCodeCurrentLabel(
						paymentData.price.voucherCode as any
					);
				}
			);
		}
	};

	useEffect(() => {
		if (totalAmount === 0) {
			getPaymentData();
		} else {
			setTotalAmountLabel(totalAmount);
			setVoucherCodeCurrentLabel(voucherCodeCurrent);
		}
		setShow(false);
		setVoucherCode('');
		activateApplyVoucher(true);
		setErrorCode('');
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen]);

	useEffect(() => {
		if (subscriptionPaymentStatus !== -1) {
			const intervalId = setInterval(() => {
				setIntervalDelay((prevDelay) => prevDelay + 2000);
				// setRetryNumber((prevDelay) => prevDelay + 1);
				checkPayStatus();
			}, intervalDelay);
			if (subscriptionPaymentStatus !== 3) {
				refetch && refetch();
				clearInterval(intervalId);
			}
			return () => clearInterval(intervalId);
		} else {
			if (isOpen) {
				checkPayStatus();
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		subscriptionPaymentStatus,
		intervalDelay,
		currentSubscriptionPaymentId
	]);

	const onResetVoucher = async () => {
		setVoucherCode('');
		setTotalAmountLabel(totalAmount);
		activateApplyVoucher(true);
		setErrorCode('');
		setLoadingVoucher(false);
	};

	const validateVoucher = async (code: string) => {
		setLoadingVoucher(true);
		await axios
			.get(
				api.getSubscriptionCalculate(
					communityPartnerId as string,
					selectedPlanId as string,
					code,
					selectedPlanType as number
				)
			)
			.then(async (res) => {
				activateApplyVoucher(code ? false : true);
				setVoucherCode(code);
				setErrorCode('');
				setTotalAmountLabel(res?.data?.totalAmount);
				setLoadingVoucher(false);
			})
			.catch((error) => {
				setErrorCode(t(`error.${error.response.data.detail}`));
				activateApplyVoucher(true);
				setVoucherCode('');
				setLoadingVoucher(false);
			});
	};

	return (
		<ModalBox isOpen={isOpen} size="lg">
			<div className="d-flex align-items-center gap-2 mb-4">
				<div>
					<FontAwesome
						icon={'credit-card'}
						className={'text-primary'}
						size="2x"
						swapOpacity
					/>
				</div>
				<div>
					<span className="fw-500 fs-base">
						{t('text.choose-your-payment-method')}
					</span>
				</div>
			</div>
			<div>
				<div className="d-flex flex-column">
					<div className="d-flex flex-column gap-1">
						<span className="fs-sm fw-400">
							{t('text.total-price')}
						</span>
						{/* <span className="fs-small fw-400">
							{t(
								'text.with-this-subscription-you-can-support-other-community-partners'
							)}
						</span> */}
					</div>
					<div className="my-4 pb-3 border-bottom">
						<div className="d-flex">
							<div className="d-flex flex-column text-end pe-3 me-3 border-end border-2 text-primary">
								<h5 className="h5 mb-0">
									{currencyFormatter(totalAmountLabel)}
								</h5>

								<span className="fw-300">
									{t('title.included')}
								</span>
							</div>
							<div className="text-gray-3 text-start d-flex flex-column">
								{/* <span className="fw-300 lh-base">
									{t('title.per')}
								</span> */}
								<span className="fs-base">
									{`${subscriptionDuration ?? ''} ${t(
										'title.days'
									)}`}
								</span>
							</div>
						</div>
					</div>
					{isVoucher && voucherCodeCurrentLabel && (
						<div className="d-flex align-items-center gap-3 mb-3">
							<span className="fs-sm fw-700">
								{t('label.current-voucher')}:
							</span>
							<span>{voucherCodeCurrentLabel}</span>
						</div>
					)}
					{!isLoadingPaymentMethodList && isVoucher && !show && (
						<div className="d-flex align-items-center gap-3 w-100 w-xs-auto mb-3">
							<div
								className="d-flex align-items-center gap-1 text-green cursor-pointer"
								onClick={() => setShow(true)}>
								<FontAwesome icon="plus" />
								{t('forms.add-voucher-code')}
							</div>
						</div>
					)}
					{isVoucher && show && (
						<div className="mb-3">
							<div className="d-flex align-items-center gap-3 w-50">
								<div
									className={clx(
										errorCode !== '' && styles.error,
										styles.inputboxWrapper,
										'inputbox d-flex align-items-center flex-fill gap-3'
									)}>
									<input
										id="code"
										type="text"
										disabled={
											!canApplyVoucher || loadingVoucher
										}
										placeholder={t(
											'placeholder.enter-code-here'
										)}
										value={voucherCode}
										onChange={(e) => {
											if (
												canApplyVoucher ||
												!loadingVoucher
											) {
												setVoucherCode(e.target.value);
											}
										}}
										className={styles.inputbox}
									/>
								</div>

								{canApplyVoucher && (
									<Button
										type="button"
										color={Colors.primary}
										disabled={
											loadingVoucher || voucherCode === ''
												? true
												: false
										}
										className="flex-shrink-0 me-2"
										label={
											loadingVoucher
												? (t('button.saving') as string)
												: (t('button.check') as string)
										}
										onClick={() => {
											validateVoucher(voucherCode);
										}}
									/>
								)}
								{!canApplyVoucher && (
									<Button
										type="button"
										color={Colors.danger}
										disabled={loadingVoucher || disabled}
										className="flex-shrink-0 me-2"
										label={
											loadingVoucher
												? (t('button.saving') as string)
												: (t('button.remove') as string)
										}
										onClick={onResetVoucher}
									/>
								)}
							</div>
							{errorCode !== '' && (
								<div className="invalid-feedback d-block">
									{errorCode}
								</div>
							)}
						</div>
					)}
					{canApplyVoucher ? (
						<div className="d-flex flex-column gap-4">
							{!isLoadingPaymentMethodList &&
								paymentMethodList?.map((item) => {
									return (
										<div
											key={item.paymentMethodId}
											className={
												isLoading || disabled
													? styles.disabledCardPayment
													: styles.cardPayment
											}>
											<div className="d-flex align-items-center justify-content-between">
												<div className="d-flex flex-column gap-2">
													<div className="d-flex align-items-center gap-2">
														<FontAwesome
															icon={'credit-card'}
															className={
																isLoading ||
																disabled
																	? 'text-gray-2'
																	: 'text-primary'
															}
															size="1x"
															swapOpacity
														/>
														<span
															className={`fs-5 fw-500 ${
																isLoading ||
																disabled
																	? 'text-gray-2'
																	: 'text-gray-4'
															}`}>
															{`${item.title}  ${
																item.last4 ?? ''
															}`}
														</span>
													</div>
													<div className="mt-2">
														{item.expYear &&
															item.expYear !==
																'0' && (
																<span
																	className={`fw-400 fs-sm 	${
																		isLoading ||
																		disabled
																			? 'text-gray-2'
																			: 'text-gray-3'
																	} mx-3`}>
																	{`Expired ${item.expMonth}/${item.expYear}`}
																</span>
															)}
														{item.expYear ===
															'0' && (
															<span className="fw-400 fs-sm text-gray-3 mx-3">
																{item.email}
															</span>
														)}
													</div>
												</div>
												<div>
													<Button
														onClick={() => {
															if (
																!disabled &&
																!isLoading
															) {
																onClickPayment(
																	item.paymentMethodId
																);
															}
														}}
														color={
															disabled ||
															isLoading
																? Colors[
																		'gray-2'
																  ]
																: Colors[
																		'white-primary'
																  ]
														}>
														{t(
															'button.pay-with-this'
														)}
													</Button>
												</div>
											</div>
										</div>
									);
								})}
							{isLoadingPaymentMethodList &&
								[1, 2, 3].map((item) => {
									return (
										<div
											key={item}
											className={
												styles.cardPaymentSkeleton
											}></div>
									);
								})}
							{!isLoadingPaymentMethodList && (
								<div
									className={
										disabled || isLoading
											? styles.disabledCardPayment
											: styles.addCardPayment
									}
									onClick={onClickAddPayment}>
									<div className="d-flex align-items-center gap-2">
										<FontAwesome
											icon="plus"
											className={
												disabled || isLoading
													? 'text-gray-2'
													: 'text-primary'
											}
											size="1x"
											swapOpacity
										/>
										<span
											className={`fs-5 fw-500  ${
												disabled || isLoading
													? 'text-gray-2'
													: 'text-primary'
											}`}>
											{t('button.add-payment-method')}
										</span>
									</div>

									<div className="mt-2">
										<span
											className={`fw-400 fs-sm mx-3 ${
												disabled || isLoading
													? 'text-gray-2'
													: 'text-gray-3'
											}`}>
											{t('button.add-new-payment-method')}
										</span>
									</div>
								</div>
							)}
						</div>
					) : (
						<div
							className={
								disabled || loadingVoucher
									? styles.disabledCardPayment
									: styles.selected
							}
							style={{ cursor: 'pointer' }}
							onClick={onClickApplyVoucher}>
							<div className="d-flex align-items-center gap-2">
								<span
									className={`fs-5 fw-500  ${
										disabled || loadingVoucher
											? 'text-gray-2'
											: 'text-primary'
									}`}>
									{t('wizard.proceed-to-payment')}
								</span>
							</div>
							<div className="mt-2">
								<span
									className={`fw-400 fs-sm ${
										disabled || isLoading
											? 'text-gray-2'
											: 'text-gray-3'
									}`}>
									{t('text.pm-voucher-pay')}
								</span>
							</div>
						</div>
					)}
				</div>
			</div>
			<ModalBoxFooter className="pt-3 d-flex justify-content-center border-top mt-4">
				<Button
					color={Colors['white-red']}
					label={t('button.cancel') as string}
					onClick={() => {
						navigate(urlBack);
						refetch && refetch();
						setTimeout(() => onClose?.(), 400);
					}}
				/>
			</ModalBoxFooter>
		</ModalBox>
	);
};
